/**
 * Universal Search Service
 * Client for GET /api/search — Boolean full-text search over all of the user's
 * threads and documents (PostgreSQL FTS, user-scoped server-side).
 */

import { supabase, isSupabaseConfigured, getCurrentUserId } from "@/lib/supabase";
import { authHeaders } from "@/lib/authToken";

export interface UniversalSearchResult {
  resultType: "thread" | "document";
  id: string;
  title: string;
  /** ts_headline output — matched terms wrapped in <mark>…</mark> (render escaped!) */
  snippet: string;
  spaceId: string | null;
  spaceName: string | null;
  topicId: string | null;
  topicName: string | null;
  updatedAt: string | null;
  rank: number;
}

export interface UniversalSearchResponse {
  results: UniversalSearchResult[];
  total: number;
  query: string;
}

export interface UniversalSearchFilters {
  threads: boolean;
  documents: boolean;
  /** yyyy-mm-dd or empty */
  dateFrom: string;
  dateTo: string;
  spaceIds: string[];
  topicIds: string[];
}

export const DEFAULT_SEARCH_FILTERS: UniversalSearchFilters = {
  threads: true,
  documents: true,
  dateFrom: "",
  dateTo: "",
  spaceIds: [],
  topicIds: [],
};

export function countActiveFilters(f: UniversalSearchFilters): number {
  let n = 0;
  if (!(f.threads && f.documents)) n += 1;
  if (f.dateFrom) n += 1;
  if (f.dateTo) n += 1;
  if (f.spaceIds.length > 0) n += 1;
  if (f.topicIds.length > 0) n += 1;
  return n;
}

export interface FilterSpace {
  id: string;
  name: string;
}

export interface FilterTopic {
  id: string;
  name: string;
  spaceId: string;
}

class UniversalSearchService {
  async search(
    query: string,
    filters: UniversalSearchFilters,
    signal?: AbortSignal,
    limit = 50
  ): Promise<UniversalSearchResponse> {
    const q = query.trim();
    if (!q) return { results: [], total: 0, query };

    const params = new URLSearchParams({ q, limit: String(limit) });
    if (!(filters.threads && filters.documents)) {
      params.set("type", filters.threads ? "threads" : "documents");
    }
    if (filters.spaceIds.length > 0) params.set("spaceId", filters.spaceIds.join(","));
    if (filters.topicIds.length > 0) params.set("topicId", filters.topicIds.join(","));
    if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
    if (filters.dateTo) params.set("dateTo", filters.dateTo);

    const headers = await authHeaders(false);
    const res = await fetch(`/api/search?${params.toString()}`, { headers, signal });
    if (!res.ok) {
      throw new Error(res.status === 429 ? "Too many searches — slow down a moment" : `Search failed (${res.status})`);
    }
    return res.json();
  }

  /** User-owned, non-deleted spaces for the filter panel. */
  async listFilterSpaces(): Promise<FilterSpace[]> {
    if (!isSupabaseConfigured) return [];
    const userId = await getCurrentUserId();
    if (!userId) return [];
    const { data, error } = await supabase
      .from("mastermind")
      .select("id, name")
      .eq("user_id", userId)
      .is("deleted_at", null)
      .eq("space_status", "active")
      .order("name");
    if (error) {
      console.error("universalSearch listFilterSpaces error:", error);
      return [];
    }
    return (data ?? []).map((s: { id: string; name: string | null }) => ({
      id: s.id,
      name: s.name || "Untitled Space",
    }));
  }

  /** Topics within the given spaces (or all of the user's spaces when none selected). */
  async listFilterTopics(spaceIds: string[]): Promise<FilterTopic[]> {
    if (!isSupabaseConfigured) return [];
    let ids = spaceIds;
    if (ids.length === 0) {
      ids = (await this.listFilterSpaces()).map((s) => s.id);
    }
    if (ids.length === 0) return [];
    const { data, error } = await supabase
      .from("topics")
      .select("id, name, space_id")
      .in("space_id", ids)
      .order("name");
    if (error) {
      console.error("universalSearch listFilterTopics error:", error);
      return [];
    }
    return (data ?? []).map((t: { id: number | string; name: string | null; space_id: string }) => ({
      id: String(t.id),
      name: t.name || "Untitled Topic",
      spaceId: t.space_id,
    }));
  }
}

export const universalSearchService = new UniversalSearchService();
